/**
 * The trial countdown chip (PPL-358) — the chrome's one word about the trial.
 *
 * Three registers off one served date, styled to match how loudly each is
 * allowed to speak: quiet is barely chrome, mid picks up the magenta tint, and
 * clear — the last three days — goes gold with the moon glyph the paused state
 * uses everywhere else. Never a modal, never an interstitial; tapping it opens
 * the premium page in its trialing variant, tagged as the referrer so the
 * funnel can tell this entry point from a cold visit.
 *
 * It REPLACES the "go premium" CTA while trialing: a trialist IS on premium,
 * and a cold upsell aimed at someone who already has the thing reads as the
 * product not knowing them (the brief's "trialing variant, not cold upsell").
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Moon, Sparkle } from './glyphs';
import { RT } from './tokens';
import { PaywallSource } from '../../services/monetizationEvents';
import { countdownLabel, type TrialCountdown } from '../../services/trialService';

const REGISTER_STYLE = {
  quiet: {
    background: 'rgba(255,255,255,0.035)',
    border: `1px solid ${RT.border}`,
    color: RT.dimSolid,
    fontWeight: 500,
  },
  mid: {
    background: RT.magentaTint,
    border: `1px solid ${RT.magentaTintBorder}`,
    color: RT.muted,
    fontWeight: 500,
  },
  clear: {
    background: RT.goldTint,
    border: `1px solid ${RT.goldTintBorder}`,
    color: '#fff',
    fontWeight: 600,
  },
} as const;

export default function TrialCountdownChip({ countdown }: { countdown: TrialCountdown }) {
  const navigate = useNavigate();
  if (!countdown.isTrialing) return null;
  const reg = countdown.register;
  const st = REGISTER_STYLE[reg];
  return (
    <button
      onClick={() => navigate(`/premium?source=${PaywallSource.TRIAL_COUNTDOWN}`)}
      aria-label={`Premium trial: ${countdownLabel(countdown)}`}
      style={{
        appearance: 'none',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '5px 11px 5px 8px',
        borderRadius: 999,
        fontSize: 11,
        whiteSpace: 'nowrap',
        fontFamily: 'inherit',
        ...st,
      }}
    >
      {reg === 'clear' ? (
        <Moon size={12} />
      ) : (
        <span style={{ opacity: reg === 'quiet' ? 0.55 : 0.85, display: 'inline-flex' }}>
          <Sparkle size={10} />
        </span>
      )}
      <span>{countdownLabel(countdown)}</span>
    </button>
  );
}
